import { FacetValue, ResultFacet } from '@/types/SearchFacet';
import {
    getArchivedSearchFacets,
    getRawArchivedSearchFacets,
    getRawUpcomingSearchFacets,
    getSelectedOptionFacetValues,
    getSoldFacetsSelectedCreator,
    getUpcomingSearchFacets,
} from '@/redux/modules/searchFacetAggregates.selectors';
import { getSearchUiState } from '@/redux/modules/search/search.selectors';
import { GlobalState } from '@/redux/store';
import { isArchivedSearch } from '@/utils/queryParams';
import { useAppSelector } from '@/redux/hooks';
import { useMemo } from 'react';
import FacetTypes from '@/types/search/enums/facetTypes';

// Type definitions for hook parameters and return values
interface SearchUiState {
    error: boolean;
    isInitialLoading: boolean;
    isLoading: boolean;
    isZeroResults: boolean;
    itemIds: number[];
    soldItemIds: number[];
    sort: any[];
    submitted: boolean;
    success: boolean;
    totalBuyNowItems: number;
    totalFound: number;
    totalLiveItems: number;
    totalPages: number;
    totalSoldItems: number;
}

interface OptimizedSearchSelectors {
    archivedSearch: boolean;
    currentFacets: ResultFacet[];
    rawFacets: ResultFacet[];
    searchState: SearchUiState;
    selectedCreator: FacetValue | undefined;
}

/**
 * Optimized hook that batches multiple selectors and memoizes results
 * to reduce re-renders and improve performance
 */
export const useOptimizedSearchSelectors = (
    queryParams: Record<string, string | string[] | number | undefined>,
    pathname: string
): OptimizedSearchSelectors => {
    const archivedSearch = useMemo(() => isArchivedSearch(queryParams, pathname), [queryParams, pathname]);

    // Batch selector calls to reduce subscription overhead
    const searchState = useAppSelector(getSearchUiState);
    const facets = useAppSelector(getUpcomingSearchFacets);
    const archivedSearchFacets = useAppSelector(getArchivedSearchFacets);
    const archivedSearchRawFacets = useAppSelector(getRawArchivedSearchFacets);
    const upcomingRawFacets = useAppSelector(getRawUpcomingSearchFacets);
    const soldSelectedCreator = useAppSelector(getSoldFacetsSelectedCreator);

    const upcomingSelectedCreator = useAppSelector((state: GlobalState) =>
        getSelectedOptionFacetValues(state, archivedSearch, FacetTypes.CREATOR_ID)
    );

    // Memoize derived values to prevent unnecessary recalculations
    const memoizedValues = useMemo(() => {
        const rawFacets = archivedSearch ? archivedSearchRawFacets : upcomingRawFacets;
        const currentFacets = archivedSearch ? archivedSearchFacets : facets;
        const selectedCreator =
            !searchState.totalFound && !archivedSearch ? soldSelectedCreator : upcomingSelectedCreator;

        return {
            archivedSearch,
            currentFacets,
            rawFacets,
            searchState,
            selectedCreator,
        };
    }, [
        archivedSearch,
        archivedSearchFacets,
        archivedSearchRawFacets,
        facets,
        searchState,
        soldSelectedCreator,
        upcomingRawFacets,
        upcomingSelectedCreator,
    ]);

    return memoizedValues;
};
